import { normalizeChatMessage } from './chatPolicy';
import { InvalidMultiplayerPacketError } from './schemaModel.js';
import {remoteGore} from './remoteGore';
import {decodeWounds, localWounds} from './woundProtocol.js';
import {encodeBloodColor, decodeBloodColor} from './bloodProtocol.js';
import {getCharacterBloodColors} from '../utils/BloodColors.js';
import {bloodColorRamp} from '../../static/mod/bloodColors.js';
import {remoteWoundBudget} from './remoteSkinDamage.js';
import { localMultiplayer } from './localConfig';
import { clampMaxPlayers, joinErrorCode, JOIN_ERROR, levelHiddenOn, LOBBY_STATUS, lobbyCustomData, lobbyRoomName, normalizeLobbyCode, normalizePassword, normalizeRoomName, PLATFORM, quickPlayLobbies, randomRoomName } from './lobbyPolicy.js';
import * as PIXI from 'pixi.js';
import { Key } from '../../libs/Key';
import { timeFormat } from '../b2Editor/utils/formatString';
import { game } from '../Game'
import { Settings } from '../Settings';
import { updateLobbyUI } from '../ui/lobby';
import { promptLobbyPassword, showMultiplayerNotice, showUpdateNotice, updateLobbyBrowser } from '../ui/lobbyBrowser';
import { backendManager } from '../utils/BackendManager';
import { isMobile } from '../utils/device.js';
import { globalEvents } from '../utils/EventDispatcher';
import { localize } from '../utils/Localization';
import { getModdedPortrait } from '../utils/ModManager';
import { initHud, CHAT_AUTHOR_TYPES, HUD_STATES, processChatMessage, processSystemMessage, setMultiplayerHud, showChat, showLeaderboard, updateLeaderboard, hudState, initLeaderboard } from './hud';
import { characterFromBuffer, characterToBuffer, dataFromAdminIntroductionBuffer, dataFromChangeServerLevelBuffer, dataFromChatMessageBuffer, dataFromEndCountDownMessageBuffer, dataFromIntroductionBuffer, dataFromLevelVotesMessageBuffer, dataFromLevelWonBuffer, dataFromSelectHatMessageBuffer, dataFromSimpleMessageBuffer, dataFromStartLoadLevelBuffer, dataToAdminIntroductionBuffer, dataToChangeServerLevelBuffer, dataToChatMessageBuffer, dataToEndCountDownMessageBuffer, dataToIntroductionBuffer, dataToLevelVotesMessageBuffer, dataToLevelWonBuffer, dataToSelectHatMessageBuffer, dataToSimpleMessageBuffer, dataToStartLoadLevelBuffer } from './messagePacker';
import { multiplayerAtlas, PLAYER_STATUS, RippleCharacter } from './rippleCharacter';
import { playerPlatformModel, SIMPLE_MESSAGE_TYPES } from './schemas';
import server, { SERVER_EVENTS } from './server';

let tickID = 0
const ticksPerSecond = 20;
let syncing = false;
let lastPoseSent = 0;

export const LOBBY_STATE = {
	OFFLINE: -1,
	CONNECTING: 0,
	WAITING: 1,
	READY: 2,
	LOADING_LEVEL: 3,
	FINISHED_LOADING_LEVEL: 4,
	PLAYING: 5,
	WON_LEVEL: 6,
	VOTING: 7,
}

export const multiplayerState = {
	debug: false,
	admin: false,
	hostID: '',
	lobby: '',
	lobbyName: '',
	lobbySettings: {public: true, hasPassword: false, maxPlayers: Settings.maxMultiplayerPlayers},
	password: null, // null when someone else set it
	kickedPeers: new Set(),
	peersConnected: 0,
	misc: '',
	sendPackageID: -1,
	players: {},
	selectedLevel: '',
	selectedLevelData: null,
	currentGameState: null, // received from introduction to see if the game already started
	lobbyState: LOBBY_STATE.OFFLINE,
	endTime: 0,
	loadingSince: 0,
	vip: false,
	finishTime: -1,
	skinBlob: null,
	voteLevels: [],
	levelVoters: {},
	levelVotes: [0, 0, 0, 0],
	syncedHat: null,
	fakeUsername: `Jolly${(Math.floor(Math.random()*100000)).toString().padStart(5, '0')}`,
}


export const multiplayerHatTextureLookup = ["DirtBikeHelmet0000", "SkateHelmet0000", "RopeHelmet0000", "HelicopterHelmet0000", "PortalHelmet0000"];

window.multiplayerState = multiplayerState;

const HOST_MESSAGES = new Set([
	SIMPLE_MESSAGE_TYPES.START_COUNTDOWN,
	SIMPLE_MESSAGE_TYPES.FINISH_END_COUNTDOWN,
	SIMPLE_MESSAGE_TYPES.RETURN_TO_LOBBY,
	SIMPLE_MESSAGE_TYPES.KICKED_BY_ADMIN,
]);

let joinAttempt = 0;
let levelLoad = 0;
let publishedLobbyData = '';

export const localPlayerName = () => (localMultiplayer ? multiplayerState.fakeUsername : backendManager.userData?.username) || multiplayerState.fakeUsername;

export const localPlatform = () => (Settings.onPoki ? PLATFORM.POKI : 0) | (isMobile() ? PLATFORM.MOBILE : 0);

export const localClient = () => ({version: __VERSION__, platform: localPlatform()});

export const isUpdateAvailable = () => server.updateAvailable;

const lobbyPlatforms = () => Object.values(multiplayerState.players).reduce((flags, player) => flags | (player.platform || 0), localPlatform());

export const levelPlayableInLobby = levelData => !(levelHiddenOn(levelData) & lobbyPlatforms());

const listeners = {
	[SERVER_EVENTS.LEFT_LOBBY]: () => returnToMultiplayer('multiplayer_connectionlost'),
	[SERVER_EVENTS.NETWORK_FAILED]: () => networkFailed(),
	[SERVER_EVENTS.HOST_CHANGED]: ({id}) => hostChanged(id),
	[SERVER_EVENTS.LOBBY_UPDATED]: ({lobbyInfo}) => { applyLobbyInfo(lobbyInfo); updateLobbyUI(); },
	[SERVER_EVENTS.PLAYER_JOINED]: ({id}) => playerJoined(id),
	[SERVER_EVENTS.PLAYER_LEFT]: ({id}) => playerLeft(id),
	[SERVER_EVENTS.PLAYER_RECONNECTING]: ({id, reconnecting}) => playerReconnecting(id, reconnecting),
	[SERVER_EVENTS.PLAYER_INTRODUCTION]: event => playerIntroduction(event),
	[SERVER_EVENTS.SIMPLE_MESSAGE]: event => handleSimpleMessage(event),
	[SERVER_EVENTS.CHANGE_LEVEL]: event => handleChangeLevel(event),
	[SERVER_EVENTS.START_LOAD_LEVEL]: event => handleStartLoadLevel(event),
	[SERVER_EVENTS.RECEIVE_SKIN]: event => handleReceiveSkin(event),
	[SERVER_EVENTS.LEVEL_WON]: event => handleReceiveLevelWon(event),
	[SERVER_EVENTS.CHAT_MESSAGE]: event => handleReceiveChatMessage(event),
	[SERVER_EVENTS.END_COUNTDOWN]: event => handleEndCountDownMessage(event),
	[SERVER_EVENTS.LEVEL_VOTES]: event => handleLevelVotesMessage(event),
	[SERVER_EVENTS.SELECT_HAT]: event => handleSelectHatMessage(event),
	[SERVER_EVENTS.PLAYER_PLATFORM]: ({peer, buffer}) => {
		const player = multiplayerState.players[peer];
		if (player) player.platform = playerPlatformModel.fromBuffer(buffer).flags & (PLATFORM.POKI | PLATFORM.MOBILE);
	},
	[SERVER_EVENTS.WOUNDS]: event => handleReceiveWounds(event),
	[SERVER_EVENTS.BLOOD_COLOR]: event => handleReceiveBloodColor(event),
};
let listening = false;

export const startMultiplayer = () => {
	if (!listening) {
		listening = true;
		Object.entries(listeners).forEach(([type, listener]) => globalEvents.addEventListener(type, listener));
	}
	if (multiplayerState.lobbyState === LOBBY_STATE.OFFLINE) initHud();
	prepareSkinForSending();
	return server.connect();
}

export const isMultiplayerConnected = () => !!server.n;

export const listLobbies = () => server.listLobbies();

const commercialBreak = async () => {
	try {
		await PokiSDK.commercialBreak();
	} catch (error) {
		console.warn('Commercial break failed', error);
	}
}

const lobbySettings = ({public: isPublic = true, password = '', maxPlayers = Settings.maxMultiplayerPlayers} = {}) => ({
	public: Boolean(isPublic),
	password: normalizePassword(password),
	maxPlayers: clampMaxPlayers(maxPlayers, Settings.maxMultiplayerPlayers),
});

const currentLobbyData = () => lobbyCustomData({
	version: __VERSION__,
	name: multiplayerState.lobbyName,
	host: localPlayerName(),
	level: multiplayerState.selectedLevelData,
	status: multiplayerState.lobbyState >= LOBBY_STATE.LOADING_LEVEL ? LOBBY_STATUS.PLAYING : LOBBY_STATUS.LOBBY,
});

const publishLobbyData = () => {
	if (!server.isHost) return;
	const customData = currentLobbyData();
	const serialized = JSON.stringify(customData);
	if (serialized === publishedLobbyData) return;
	publishedLobbyData = serialized;
	server.updateLobby({customData});
}

export const currentRoomName = () => lobbyRoomName({name: multiplayerState.lobbyName}, multiplayerState.lobby, Settings.onPoki);

const applyLobbyInfo = lobbyInfo => {
	multiplayerState.lobbyName = normalizeRoomName(lobbyInfo?.customData?.name);
	multiplayerState.lobbySettings = {
		public: Boolean(lobbyInfo?.public),
		hasPassword: Boolean(lobbyInfo?.hasPassword),
		maxPlayers: lobbyInfo?.maxPlayers || Settings.maxMultiplayerPlayers,
	};
}

const showJoinError = error => {
	const notices = {
		[JOIN_ERROR.NOT_FOUND]: 'multiplayer_error_notfound',
		[JOIN_ERROR.FULL]: 'multiplayer_error_full',
		[JOIN_ERROR.LOBBY_OUTDATED]: 'multiplayer_error_oldlobby',
	};
	if (error === JOIN_ERROR.UPDATE_REQUIRED) showUpdateNotice('multiplayer_error_update');
	else showMultiplayerNotice(notices[error] || 'multiplayer_error_connection');
}

const enterLobby = async (connect, onError = showJoinError) => {
	const attempt = ++joinAttempt;
	clearLobbyState();
	startMultiplayer();
	multiplayerState.lobbyState = LOBBY_STATE.CONNECTING;
	game.gameState = game.GAMESTATE_LOBBY;
	game.ui.setMainMenuActive('lobby');
	updateLobbyUI();
	try {
		await commercialBreak();
		const current = () => attempt === joinAttempt;
		if (!current()) return;
		// Check current() before each request: whatever cancelled this attempt queued its leave after them.
		const lobbyInfo = await connect(current);
		if (current() && lobbyInfo) didJoinLobby(lobbyInfo);
	} catch (error) {
		if (attempt !== joinAttempt) return;
		console.warn('Could not enter multiplayer lobby', error);
		returnToMultiplayer();
		onError(joinErrorCode(error));
	}
}

export const joinLobby = (code, password = '') => {
	code = normalizeLobbyCode(code);
	if (!code) return showMultiplayerNotice('multiplayer_error_notfound');
	password = normalizePassword(password);
	return enterLobby(async () => {
		return server.joinLobby(code, password);
	}, error => {
		if (error === JOIN_ERROR.WRONG_PASSWORD) promptLobbyPassword(code, Boolean(password));
		else if (error === JOIN_ERROR.UPDATE_REQUIRED) showUpdateNotice('multiplayer_error_update', code);
		else showJoinError(error);
	});
}

export const createLobby = settings => {
	const options = lobbySettings(settings);
	return enterLobby(async () => {
		multiplayerState.lobbyName = normalizeRoomName(settings?.name) || randomRoomName();
		const lobbyInfo = await server.createLobby({...options, password: options.password || undefined, customData: currentLobbyData()});
		multiplayerState.password = options.password;
		return lobbyInfo;
	});
}

export const quickJoinLobby = () => enterLobby(async current => {
	const lobbies = quickPlayLobbies(await server.listLobbies(), localClient());
	for (const lobby of lobbies.slice(0, 3)) {
		if (!current()) return;
		try {
			return await server.joinLobby(lobby.code);
		} catch (error) {
			if (![JOIN_ERROR.FULL, JOIN_ERROR.NOT_FOUND, JOIN_ERROR.WRONG_PASSWORD, JOIN_ERROR.UPDATE_REQUIRED, JOIN_ERROR.LOBBY_OUTDATED].includes(error.code)) throw error;
		}
	}
	if (!current()) return;
	multiplayerState.lobbyName = randomRoomName();
	const lobbyInfo = await server.createLobby({...lobbySettings(), password: undefined, customData: currentLobbyData()});
	multiplayerState.password = '';
	return lobbyInfo;
});

export const autoConnectLobby = id => id ? joinLobby(id) : createLobby();

// Leave password undefined to keep the current one.
export const updateLobbySettings = async ({name, password, ...settings}) => {
	if (!server.isHost) return false;
	const {password: _, ...options} = lobbySettings(settings);
	options.maxPlayers = Math.max(options.maxPlayers, multiplayerState.peersConnected + 1);
	if (password !== undefined) options.password = normalizePassword(password);
	const previousName = multiplayerState.lobbyName;
	multiplayerState.lobbyName = normalizeRoomName(name) || previousName;
	const customData = currentLobbyData();
	const updated = await server.updateLobby({...options, customData});
	if (updated) {
		publishedLobbyData = JSON.stringify(customData);
		if (password !== undefined) multiplayerState.password = options.password;
	} else {
		multiplayerState.lobbyName = previousName;
	}
	return updated;
}

export const setLobbyStateReady = ready => {
	multiplayerState.lobbyState = ready ? LOBBY_STATE.READY : LOBBY_STATE.WAITING;

	const messageType = ready ? SIMPLE_MESSAGE_TYPES.PLAYER_READY : SIMPLE_MESSAGE_TYPES.PLAYER_NOT_READY;
	sendSimpleMessageAll(messageType);

	updateLobbyUI();
}

// openMainMenu leaves in-level overlays open.
const closeLevel = () => {
	game.pause = false;
	game.ui.hidePauseMenu();
	game.ui.hideWinScreen();
	game.ui.hideVehicleSelect();
	game.stopWorld();
	game.openMainMenu();
}

export const returnToLobby = () => {
	levelLoad++;
	resetMultiplayer();
	multiplayerState.currentGameState = null;

	setMultiplayerHud('');

	closeLevel();
	game.ui.setMainMenuActive('lobby');
	game.gameState = game.GAMESTATE_LOBBY;

	showLeaderboard(false);

	publishLobbyData();
	updateLobbyUI();
}

// Leaves the lobby but stays online, back on the lobby list.
export const returnToMultiplayer = notice => {
	joinAttempt++;
	clearLobbyState();
	server.leaveLobby();

	closeLevel();
	game.gameState = game.GAMESTATE_MENU;
	game.ui.setMainMenuActive('multiplayer');

	if (notice) showMultiplayerNotice(notice);
}

export const leaveMultiplayer = () => {
	joinAttempt++;
	clearLobbyState();
	server.disconnect();
	multiplayerState.skinBlob = null;
}

const destroyPlayer = player => {
	player.clear();
	player.sprite.destroy({children: true, texture: false, textureSource: false});
}

const clearLobbyState = () => {
	levelLoad++;
	localWounds.clear();
	remoteGore.clear();
	Object.values(multiplayerState.players).forEach(destroyPlayer);
	multiplayerState.players = {};
	multiplayerState.peersConnected = 0;
	multiplayerState.lobby = '';
	multiplayerState.lobbyName = '';
	multiplayerState.hostID = '';
	multiplayerState.admin = false;
	multiplayerState.password = null;
	multiplayerState.kickedPeers.clear();
	multiplayerState.syncedHat = null;
	multiplayerState.currentGameState = null;
	multiplayerState.selectedLevel = '';
	multiplayerState.selectedLevelData = null;
	multiplayerState.voteLevels = [];
	publishedLobbyData = '';
	resetMultiplayer();
	showLeaderboard(false);
	setMultiplayerHud('');
	showChat(false);
	stopSyncPlayer();
	multiplayerState.lobbyState = LOBBY_STATE.OFFLINE;
}

export const resetMultiplayer = () => {
	remoteGore.clear();
	const players = Object.values(multiplayerState.players);
	players.forEach(player => {
		player.playerState.lobbyState = LOBBY_STATE.WAITING;
		player.playerState.ready = false;
		player.playerState.status = 0;
		player.playerState.finishTime = -1;
		player.lastStatusChange = 0;
	});
	multiplayerState.endTime = 0;
	multiplayerState.loadingSince = 0;
	multiplayerState.finishTime = -1;
	multiplayerState.lobbyState = LOBBY_STATE.WAITING;
	multiplayerState.levelVoters = {};
	multiplayerState.levelVotes = [0, 0, 0, 0];
}

export const selectMultiplayerLevel = levelData => {
	if(!multiplayerState.admin) return;
	if(!levelPlayableInLobby(levelData)){
		showMultiplayerNotice('multiplayer_level_unavailable');
		return;
	}
	multiplayerState.selectedLevel = levelData.id;
	multiplayerState.selectedLevelData = levelData;
	const messageBuffer = dataToChangeServerLevelBuffer(multiplayerState.selectedLevel);
	server.sendSimpleMessageAll(messageBuffer);
	publishLobbyData();
	game.openMainMenu();
}

export const sendSimpleMessageAll = messageType => {
	const simpleMessageBuffer = dataToSimpleMessageBuffer(messageType);
	server.sendSimpleMessageAll(simpleMessageBuffer);
}

export const sendChatMessage = message => {
	if (!server.inLobby) return;
	message = normalizeChatMessage(message, Settings);
	if (!message) return;
	const chatMessageBuffer = dataToChatMessageBuffer(message);
	server.sendSimpleMessageAll(chatMessageBuffer);

	let type = CHAT_AUTHOR_TYPES.DEFAULT;
	if(multiplayerState.vip) type = CHAT_AUTHOR_TYPES.VIP;
	processChatMessage(localPlayerName(), type, multiplayerState.admin, message);
}

const networkFailed = () => {
	// A failed join is reported by enterLobby.
	if (multiplayerState.lobbyState === LOBBY_STATE.CONNECTING) return;
	if (multiplayerState.lobbyState !== LOBBY_STATE.OFFLINE) returnToMultiplayer('multiplayer_connectionlost');
	else updateLobbyBrowser();
}

const didJoinLobby = lobbyInfo => {
	multiplayerState.lobby = lobbyInfo.code;
	multiplayerState.hostID = server.leader || '';
	multiplayerState.admin = server.isHost;
	multiplayerState.lobbyState = LOBBY_STATE.WAITING;
	applyLobbyInfo(lobbyInfo);

	showChat(true);

	startSyncPlayer();

	server.connectedPeers.forEach(playerJoined);
	updateLobbyUI();
}

const hostChanged = id => {
	const previous = multiplayerState.hostID;
	multiplayerState.hostID = id;
	multiplayerState.admin = id === server.id;
	for (const [playerID, player] of Object.entries(multiplayerState.players)) player.admin = playerID === id;

	if (previous && previous !== id) {
		const name = multiplayerState.admin ? localPlayerName() : multiplayerState.players[id]?.playerState.name;
		if (name) processSystemMessage(localize('multiplayer_newhost', {name}));
	}
	if (multiplayerState.admin && multiplayerState.lobbyState === LOBBY_STATE.READY) {
		multiplayerState.lobbyState = LOBBY_STATE.WAITING;
	}
	if (multiplayerState.admin && previous !== id) takeOverHost();
	publishLobbyData();
	updateLobbyUI();
	updateLeaderboard();
}

// The old host may have left before sharing its level or ending the round.
const takeOverHost = () => {
	if (multiplayerState.selectedLevel) server.sendSimpleMessageAll(dataToChangeServerLevelBuffer(multiplayerState.selectedLevel));
	const roundRunning = [LOBBY_STATE.PLAYING, LOBBY_STATE.WON_LEVEL].includes(multiplayerState.lobbyState);
	const someoneWon = multiplayerState.lobbyState === LOBBY_STATE.WON_LEVEL
		|| Object.values(multiplayerState.players).some(player => player.playerState.lobbyState === LOBBY_STATE.WON_LEVEL);
	if (roundRunning && someoneWon) checkEndLevelTimer();
}

const playerJoined = id => {
	if (multiplayerState.lobbyState <= LOBBY_STATE.CONNECTING) return;
	if (multiplayerState.admin && multiplayerState.kickedPeers.has(id)) {
		kickPlayer(id);
		return;
	}
	if (multiplayerState.players[id]) return;
	const player = new RippleCharacter(id);
	player.admin = id === multiplayerState.hostID;
	multiplayerState.players[id] = player;

	player.playerIndex = Object.keys(multiplayerState.players).length;

	multiplayerState.peersConnected++;

	greetPlayer(id, player);

	updateLobbyUI();

	return player;
}

const greetPlayer = async (id, player) => {
	const name = localPlayerName();
	const lobbyState = multiplayerState.lobbyState;

	const introductionBuffer = multiplayerState.admin
		? dataToAdminIntroductionBuffer(name, multiplayerState.selectedLevel, lobbyState, game.selectedVehicle)
		: dataToIntroductionBuffer(name, lobbyState, game.selectedVehicle);
	server.sendIntroduction(introductionBuffer, id);
	server.sendSimpleMessage(playerPlatformModel.toBuffer({flags: localPlatform()}), id);

	// Late joiners need the introduction's game state before this.
	if (multiplayerState.admin && lobbyState >= LOBBY_STATE.LOADING_LEVEL && multiplayerState.selectedLevel) {
		server.sendSimpleMessage(dataToStartLoadLevelBuffer(multiplayerState.selectedLevel), id);
	}

	const skinBlob = await prepareSkinForSending();
	const skinBuffer = await skinBlob.arrayBuffer();
	if (multiplayerState.players[id] !== player) return;
	server.sendSkinBuffer(skinBuffer, id);
	server.sendSimpleMessage(encodeBloodColor(getCharacterBloodColors(game.selectedCharacter).start), id);
	localWounds.snapshot(game.character, buffer => server.sendSimpleMessage(buffer, id));
	// A peer can finish loading their skin after the initial vehicle selection.
	server.sendSimpleMessage(dataToSimpleMessageBuffer(SIMPLE_MESSAGE_TYPES.SELECT_VEHICLE + game.selectedVehicle), id);
	const hat = game.character?.hat;
	server.sendSimpleMessage(dataToSelectHatMessageBuffer(hat ? multiplayerHatTextureLookup.indexOf(hat.texture) : -1, hat?.hatOffsetLength || 0, hat?.hatOffsetAngle || 0), id);
}

const playerLeft = id => {
	const player = multiplayerState.players[id];
	if(player){
		multiplayerState.peersConnected--;
		destroyPlayer(player);
		delete multiplayerState.players[id];
		if (player.introduced) processSystemMessage(localize('multiplayer_playerleft', {name: player.playerState.name}));
		finishRoundIfEveryoneDone();
	}

	updateLobbyUI();
	updateLeaderboard();
}

const playerReconnecting = (id, reconnecting) => {
	const player = multiplayerState.players[id];
	if (!player) return;
	player.reconnecting = reconnecting;
	updateLobbyUI();
}

const playerIntroduction = ({peer, buffer, admin}) => {
	const player = multiplayerState.players[peer];
	if(!player) return;
	if(admin && peer !== multiplayerState.hostID) return;

	const introductionData = admin ? dataFromAdminIntroductionBuffer(buffer) : dataFromIntroductionBuffer(buffer);

	player.playerState.name = introductionData.name;
	player.playerState.lobbyState = !admin && introductionData.lobbyState === LOBBY_STATE.READY ? LOBBY_STATE.READY : LOBBY_STATE.WAITING;
	player.vehicle.initialVehicle = introductionData.selectedVehicle;

	if(admin){
		if(introductionData.lobbyState >= LOBBY_STATE.LOADING_LEVEL){
			multiplayerState.currentGameState = introductionData.lobbyState;
		}
		if(introductionData.levelID){
			fetchLevelInfo(introductionData.levelID);
		}
	}

	if (!player.introduced) {
		player.introduced = true;
		processSystemMessage(localize('multiplayer_playerjoined', {name: introductionData.name}));
	}

	updateLobbyUI();
	updateLeaderboard();
}

const handleChangeLevel = ({peer, buffer}) => {
	if (peer !== multiplayerState.hostID) return;
	const { levelID } = dataFromChangeServerLevelBuffer(buffer);
	fetchLevelInfo(levelID.trim());
}

const handleStartLoadLevel = ({peer, buffer}) => {
	if (peer !== multiplayerState.hostID || multiplayerState.lobbyState <= LOBBY_STATE.CONNECTING) return;
	const { levelID } = dataFromStartLoadLevelBuffer(buffer);
	// Only a late joiner's first load starts mid-round; a new round always starts together.
	if (multiplayerState.lobbyState >= LOBBY_STATE.LOADING_LEVEL) multiplayerState.currentGameState = null;
	startLoadLevel(levelID.trim());
}

export const allPlayersReady = () => Object.values(multiplayerState.players).every(player => player.playerState.lobbyState === LOBBY_STATE.READY);

export const adminStartLoadLevel = () => {
	if (!multiplayerState.admin) return;
	if (!levelPlayableInLobby(multiplayerState.selectedLevelData)) {
		showMultiplayerNotice('multiplayer_level_unavailable');
		return;
	}
	if((allPlayersReady() && multiplayerState.selectedLevelData) || multiplayerState.lobbyState === LOBBY_STATE.VOTING){
		const startLoadLevelBuffer = dataToStartLoadLevelBuffer(multiplayerState.selectedLevel);
		server.sendSimpleMessageAll(startLoadLevelBuffer);

		multiplayerState.currentGameState = null;
		startLoadLevel(multiplayerState.selectedLevel);
	}
}

export const adminReturnToLobby = () => {
	if (multiplayerState.admin) sendSimpleMessageAll(SIMPLE_MESSAGE_TYPES.RETURN_TO_LOBBY);
}

export const kickPlayer = id => {
	if (!multiplayerState.admin || id === server.id) return;
	multiplayerState.kickedPeers.add(id);
	server.sendSimpleMessage(dataToSimpleMessageBuffer(SIMPLE_MESSAGE_TYPES.KICKED_BY_ADMIN), id);
	server.ignorePeer(id);
	playerLeft(id);
}

export const requestGameState = () => {
	const simpleMessageBuffer = dataToSimpleMessageBuffer(SIMPLE_MESSAGE_TYPES.REQUEST_GAME_STATE);
	server.sendSimpleMessageAll(simpleMessageBuffer);
}

const startLoadLevel = async id => {
	const load = ++levelLoad;
	resetMultiplayer();

	game.gameState = game.GAMESTATE_LOADINGDATA;

	multiplayerState.lobbyState = LOBBY_STATE.LOADING_LEVEL;

	publishLobbyData();

	showLeaderboard(true);

	const players = Object.values(multiplayerState.players);
	players.forEach(player => {
		if(player.playerState.lobbyState !== LOBBY_STATE.PLAYING){
			player.playerState.lobbyState = LOBBY_STATE.LOADING_LEVEL;
		}
	});
	updateLobbyUI();

	await fetchLevelInfo(id);
	if (load !== levelLoad) return;

	const levelData = multiplayerState.selectedLevelData;
	if (!levelData || levelData.id !== id) {
		returnToMultiplayer('multiplayer_error_level');
		return;
	}

	try {
		if (!(__LOCAL_MULTIPLAYER__ && localMultiplayer && levelData.id === 'local-multiplayer-lab')) {
			await game.loadPublishedLevelData(levelData, () => {});
		} else {
			game.currentLevelData = levelData;
		}
	} catch (error) {
		console.warn('Could not load multiplayer level', error);
		if (load === levelLoad) returnToMultiplayer('multiplayer_error_level');
		return;
	}
	// The host may have sent everyone back to the lobby while this level loaded.
	if (load !== levelLoad) return;

	if(levelData.forced_vehicle){
		game.selectedVehicle = levelData.forced_vehicle;
		game.ui.playLevelFromSinglePlayer();
	}else{
		game.ui.showVehicleSelect();
	}
	game.run = false;
}

const handleSimpleMessage = ({peer, buffer}) => {
	const { type } = dataFromSimpleMessageBuffer(buffer);

	const player = multiplayerState.players[peer];
	if (!player) return;
	if (HOST_MESSAGES.has(type) && peer !== multiplayerState.hostID) return;

	switch(type){
		case SIMPLE_MESSAGE_TYPES.PLAYER_READY:
			player.playerState.lobbyState = LOBBY_STATE.READY;
			updateLobbyUI();
			break;
		case SIMPLE_MESSAGE_TYPES.PLAYER_NOT_READY:
			player.playerState.lobbyState = LOBBY_STATE.WAITING;
			updateLobbyUI();
			break;
		case SIMPLE_MESSAGE_TYPES.PLAYER_FINISHED_LOADING:
			player.playerState.lobbyState = LOBBY_STATE.FINISHED_LOADING_LEVEL;
			updateLobbyUI();
			break;
		case SIMPLE_MESSAGE_TYPES.START_COUNTDOWN:
			if (hudState === HUD_STATES.WAITING_PLAYERS) {
				multiplayerState.lobbyState = LOBBY_STATE.PLAYING;
				setMultiplayerHud(HUD_STATES.COUNTDOWN, {ping: Math.max(0, player.ping)});
				updateLobbyUI();
			} else if (multiplayerState.lobbyState === LOBBY_STATE.LOADING_LEVEL) {
				// Still picking a vehicle: start as a late joiner once the level is built.
				multiplayerState.currentGameState = LOBBY_STATE.PLAYING;
			}
			break;
		case SIMPLE_MESSAGE_TYPES.FINISH_END_COUNTDOWN:
			if(multiplayerState.endTime > 0){
				multiplayerState.endTime = 1;
			}
			break;
		case SIMPLE_MESSAGE_TYPES.RETURN_TO_LOBBY:
			if (multiplayerState.lobbyState >= LOBBY_STATE.LOADING_LEVEL) returnToLobby();
			break;
		case SIMPLE_MESSAGE_TYPES.LEVEL_FAILED:
			player.setPlayerState(PLAYER_STATUS.DEATH);
			updateLeaderboard();
			break;
		case SIMPLE_MESSAGE_TYPES.LEVEL_CHECKPOINT:
			player.setPlayerState(PLAYER_STATUS.CHECKPOINT);
			updateLeaderboard();
			break;

		case SIMPLE_MESSAGE_TYPES.VOTE_LEVEL_1:
		case SIMPLE_MESSAGE_TYPES.VOTE_LEVEL_2:
		case SIMPLE_MESSAGE_TYPES.VOTE_LEVEL_3:
		case SIMPLE_MESSAGE_TYPES.VOTE_LEVEL_4:
			if(multiplayerState.admin){
				const levelVote = type - SIMPLE_MESSAGE_TYPES.VOTE_LEVEL_1;
				multiplayerState.levelVoters[peer] = levelVote;
				multiplayerState.levelVotes = [0, 0, 0, 0];
				Object.values(multiplayerState.levelVoters).forEach(val => {
					multiplayerState.levelVotes[val]++;
				});

				const messageBuffer = dataToLevelVotesMessageBuffer(multiplayerState.levelVotes);
				server.sendSimpleMessageAll(messageBuffer);
			}
			break;

		case SIMPLE_MESSAGE_TYPES.KICKED_BY_ADMIN:
			returnToMultiplayer('multiplayer_kicked');
			break;
		case SIMPLE_MESSAGE_TYPES.REQUEST_GAME_STATE:
			if(multiplayerState.admin && multiplayerState.endTime > 0){
				const voteLevelIds = multiplayerState.voteLevels.slice(1).map(level => level?.id);
				if (voteLevelIds.length === 3 && voteLevelIds.every(Boolean)) {
					const messageBuffer = dataToEndCountDownMessageBuffer(voteLevelIds, Settings.endGameTimer - multiplayerState.endTime);
					server.sendSimpleMessage(messageBuffer, peer);
				}
			}
			break;

		default:
			if(type > SIMPLE_MESSAGE_TYPES.SELECT_VEHICLE){
				const vehicleIndex = type - SIMPLE_MESSAGE_TYPES.SELECT_VEHICLE;
				player.vehicle.selectVehicle(vehicleIndex);
			}
	}
}

const handleEndCountDownMessage = ({peer, buffer}) => {
	const player = multiplayerState.players[peer];
	if (!player || peer !== multiplayerState.hostID || multiplayerState.lobbyState < LOBBY_STATE.LOADING_LEVEL) return;

	const { levelIds, timeOffset } = dataFromEndCountDownMessageBuffer(buffer);

	const promises = levelIds.map(id => backendManager.getPublishedLevelInfo(id.trim(), {silent: true}));

	Promise.all(promises).then(voteLevels => {
		multiplayerState.voteLevels = [multiplayerState.selectedLevelData, ...voteLevels];
	}).catch(error => console.warn('Could not load vote levels', error));

	// A late joiner skips the start countdown once the round is ending.
	if (game.character) game.run = true;

	multiplayerState.endTime = Settings.endGameTimer - Math.max(0, player.ping) - timeOffset;
	if(multiplayerState.lobbyState !== LOBBY_STATE.WON_LEVEL){
		setMultiplayerHud(HUD_STATES.GAME_END_COUNTDOWN);
	}
	updateLobbyUI();
}

const handleLevelVotesMessage = ({peer, buffer}) => {
	if (peer !== multiplayerState.hostID) return;
	const { votes } = dataFromLevelVotesMessageBuffer(buffer);
	multiplayerState.levelVotes = votes;
}

const handleSelectHatMessage = ({peer, buffer}) => {

	const player = multiplayerState.players[peer];

	const { hat, hatOffsetLength, hatOffsetAngle } = dataFromSelectHatMessageBuffer(buffer);

	const hatTexture = multiplayerHatTextureLookup[hat];

	player?.selectHat(hatTexture, hatOffsetLength, hatOffsetAngle);

}

const handleReceiveWounds = ({peer, buffer}) => {
	const packet = decodeWounds(buffer);
	multiplayerState.players[peer]?.receiveWounds(packet);
};

const handleReceiveBloodColor = ({peer, buffer}) => {
	const color = decodeBloodColor(buffer);
	const player = multiplayerState.players[peer];
	if (player) player.bloodColors = bloodColorRamp(color);
};

const handleReceiveSkin = ({peer, buffer}) => {
	const player = multiplayerState.players[peer];
	if(!player) return;

	const blob = new Blob( [ buffer ], { type: "image/png" } );
    const urlCreator = window.URL || window.webkitURL;
    const imageUrl = urlCreator.createObjectURL( blob );
	player.skinBlob = blob;
	player.loadSkin(imageUrl)
		.catch(error => console.warn('Could not load multiplayer skin', error))
		.finally(() => urlCreator.revokeObjectURL(imageUrl));
	updateLobbyUI();
	updateLeaderboard();
}

export const startSyncPlayer = () => {
	syncing = true;
	lastPoseSent = 0;
}

export const stopSyncPlayer = () => {
	syncing = false;
}

// Called from the game loop rather than a timer, so every pose covers the same
// number of physics steps and carries the time it was simulated.
const syncPlayer = now => {
	if(!syncing || now - lastPoseSent < 1000 / ticksPerSecond - 8) return;
	lastPoseSent = now;
	multiplayerState.misc = `hasChar:${(!!game.character).toString()}`
	if(!game.character || !server.inLobby) return;

	const buffer = characterToBuffer(game.character, tickID, now);
	server.sendCharacterData(buffer);
	localWounds.flush(game.character, wounds => server.sendSimpleMessageAll(wounds));

	const { hat } = game.character;

	if(hat !== multiplayerState.syncedHat){
		multiplayerState.syncedHat = hat;
		const hatIndex = hat ? multiplayerHatTextureLookup.indexOf(hat.texture) : -1;
		const hatOffsetLength = hat ? hat.hatOffsetLength : 0;
		const hatOffsetAngle = hat ? hat.hatOffsetAngle : 0;
		const messageBuffer = dataToSelectHatMessageBuffer(hatIndex, hatOffsetLength, hatOffsetAngle);
		server.sendSimpleMessageAll(messageBuffer);
	}

	tickID++;
	if(tickID > 255){
		tickID = 0;
	}

	multiplayerState.sendPackageID = tickID;
}

export const updateMultiplayer = () => {
	syncPlayer(performance.now());
	remoteWoundBudget.beginFrame();
	const updateRemoteEffects = multiplayerState.peersConnected > 0 || remoteGore.active.length > 0;
	if (updateRemoteEffects) {
		const gravity = game.world?.GetGravity();
		remoteGore.beginFrame(game.character ? game.editor.textures : null, performance.now(), Settings, window.innerWidth, window.innerHeight, (gravity?.x || 0) * Settings.PTM, (gravity?.y || 0) * Settings.PTM);
	}

	const data = server.getCharacterDataToProcess();
		data.forEach(data => {
			const player = multiplayerState.players[data.playerID];
			if(player){
				player.ping = data.ping;
				const time = data.time;
				let characterData;
				try {
					characterData = characterFromBuffer(data.buffer);
				} catch (error) {
					if (error instanceof InvalidMultiplayerPacketError) return;
					throw error;
				}

				if(!game.run && [HUD_STATES.WAITING_PLAYERS, HUD_STATES.COUNTDOWN].includes(hudState)){
					// ************ offset player at the start of a match
					const spreadOffset = -4 * (player.playerIndex + 1);
					characterData.main.forEach(data => {
						if(player.sprites){
							data.x += spreadOffset * Math.cos(player.sprites.head.rotation);
							data.y += spreadOffset * Math.sin(player.sprites.head.rotation);
						}
					});
					// ***************************************************
				}
				multiplayerState.players[data.playerID].processServerData(characterData, time);
			}
		});

	try{
		if(game.character){
			for(let playerID in multiplayerState.players){

				const player = multiplayerState.players[playerID];

				if(!player.addedToGame){
					const targetTexture = game.character.lookupObject._bodies[0]?.mySprite || game.editor.textures.children[game.editor.textures.children.length-1];
					const index = targetTexture.parent.getChildIndex(targetTexture);
					targetTexture.parent.addChildAt(player.sprite, index);
					player.addedToGame = true;
					initLeaderboard();
					updateLeaderboard();
				}

				if(player.connected && player.addedToGame) player.interpolatePosition();
			}

			if([LOBBY_STATE.PLAYING, LOBBY_STATE.WON_LEVEL].includes(multiplayerState.lobbyState) && multiplayerState.endTime > 0){
				multiplayerState.endTime -= game.editor.deltaTime;
				if(multiplayerState.endTime < 0){
					multiplayerState.lobbyState = LOBBY_STATE.VOTING;
					setMultiplayerHud(HUD_STATES.PICK_NEXT_LEVEL);
				}
			}
		}
	} catch(e){
		console.log('ERROR MULTIPLAYER', e);
		// stopSyncPlayer();
	}



	if (updateRemoteEffects) remoteGore.endFrame();

	if(multiplayerState.admin && multiplayerState.lobbyState === LOBBY_STATE.LOADING_LEVEL && hudState === HUD_STATES.WAITING_PLAYERS){
		const now = performance.now();
		if (!multiplayerState.loadingSince) multiplayerState.loadingSince = now;
		const everyoneLoaded = Object.values(multiplayerState.players).every(player => player.playerState.lobbyState === LOBBY_STATE.FINISHED_LOADING_LEVEL);
		// Players still picking a vehicle join the running round when they are done.
		if(everyoneLoaded || now - multiplayerState.loadingSince > Settings.multiplayerLoadTimeout){
			sendSimpleMessageAll(SIMPLE_MESSAGE_TYPES.START_COUNTDOWN);
			multiplayerState.lobbyState = LOBBY_STATE.PLAYING;
			setMultiplayerHud(HUD_STATES.COUNTDOWN, {ping:0});
		}
	}

	if(Key.isDown(Key.CTRL) && Key.isPressed(Key.I)){
		if(multiplayerState.debug){
			document.body.removeChild(debugWindow);
		}else{
			document.body.appendChild(debugWindow);
		}
		multiplayerState.debug = !multiplayerState.debug;
	}

	if(multiplayerState.debug) updateDebugData();
}


const prepareSkinForSending = async () => {

	if(multiplayerState.skinBlob) return multiplayerState.skinBlob;

	const skinCanvas = document.createElement('canvas');
	skinCanvas.width = skinCanvas.height = 1024;
	const skinContext = skinCanvas.getContext('2d', {alpha:true});

	const skin = game.selectedCharacter;

	//
	const profilePic = await getModdedPortrait(`profile${skin+1}.png`, 'assets/images/portraits/');
	const profileImage = new Image();
	await new Promise(resolve => {
		profileImage.src = profilePic;
		profileImage.onload = resolve;
	});

	// draw parts
	const targetSkinFrame = String(skin).padStart(4, '0');
	Object.keys(multiplayerAtlas.frames).forEach(partKey => {
		const targetFrame = (partKey.startsWith('Normal') || partKey.startsWith('Mouth')) ? targetSkinFrame : '0000';
		const resourceName = `${partKey}${targetFrame}`;
		const {x, y} = multiplayerAtlas.frames[partKey].frame;

		let imageResource, sourceFrame;
		if(partKey === 'profile'){
			imageResource = profileImage;
			sourceFrame = profileImage;
		} else {
			const texture = PIXI.Texture.from(resourceName);
			imageResource = texture.source.resource;
			sourceFrame = texture.frame;
		}

		skinContext.drawImage(imageResource, sourceFrame.x, sourceFrame.y, sourceFrame.width, sourceFrame.height, x, y, sourceFrame.width, sourceFrame.height);
	});

	await new Promise(resolve => {
		skinCanvas.toBlob(blob => {
			multiplayerState.skinBlob = blob;
			resolve();
		}, 'image/png')
	});

	updateLobbyUI();

	return multiplayerState.skinBlob;
}

const fetchLevelInfo = async id => {
	multiplayerState.selectedLevel = id;

	try{
		const levelData = __LOCAL_MULTIPLAYER__ && localMultiplayer && id === 'local-multiplayer-lab'
			? (await import('./localLab')).createLocalLevel()
			: await backendManager.getPublishedLevelInfo(id, {silent: true});
		if(multiplayerState.selectedLevel === id){
			multiplayerState.selectedLevelData = levelData;
			updateLobbyUI();
		}
	}catch(err){
		console.warn('Could not load multiplayer level info', err);
	}
}

export const sendLevelWon = time => {
	setMultiplayerHud(HUD_STATES.GAME_WIN_CAM);
	const messageBuffer = dataToLevelWonBuffer(time);
	server.sendSimpleMessageAll(messageBuffer);

	multiplayerState.finishTime = time;

	multiplayerState.lobbyState = LOBBY_STATE.WON_LEVEL;

	updateLeaderboard();

	checkEndLevelTimer();
}

export const sendGameOver = () => {
	sendSimpleMessageAll(SIMPLE_MESSAGE_TYPES.LEVEL_FAILED);
}

export const sendCheckpoint = () => {
	sendSimpleMessageAll(SIMPLE_MESSAGE_TYPES.LEVEL_CHECKPOINT);
}

const handleReceiveLevelWon = ({peer, buffer}) => {
	const player = multiplayerState.players[peer];
	if (!player) return;

	const { time } = dataFromLevelWonBuffer(buffer);

	player.playerState.lobbyState = LOBBY_STATE.WON_LEVEL;
	player.playerState.finishTime = time;

	updateLeaderboard();

	checkEndLevelTimer();
}

const checkEndLevelTimer = () => {
	if(!multiplayerState.admin) return;
	if(multiplayerState.endTime){
		finishRoundIfEveryoneDone();
		return;
	}
	multiplayerState.endTime = Settings.endGameTimer;

	if(multiplayerState.lobbyState !== LOBBY_STATE.WON_LEVEL){
		setMultiplayerHud(HUD_STATES.GAME_END_COUNTDOWN);
	}

	backendManager.getRandomPublishedLevels(12).then(levels => {
		if (!multiplayerState.admin || !multiplayerState.endTime) return;
		const playable = levels.filter(levelPlayableInLobby);
		const data = [...playable, ...levels.filter(level => !playable.includes(level))].slice(0, 3);
		multiplayerState.voteLevels = [multiplayerState.selectedLevelData, ...data];
		const voteLevelIds = data.map(level => level.id);
		const messageBuffer = dataToEndCountDownMessageBuffer(voteLevelIds, Settings.endGameTimer - multiplayerState.endTime);
		server.sendSimpleMessageAll(messageBuffer);
	}).catch(error => console.warn('Could not load vote levels', error));

	finishRoundIfEveryoneDone();
}

const finishRoundIfEveryoneDone = () => {
	if(!multiplayerState.admin || multiplayerState.endTime <= 1000) return;
	const everyoneFinished = multiplayerState.lobbyState === LOBBY_STATE.WON_LEVEL
		&& Object.values(multiplayerState.players).every(player => player.playerState.lobbyState === LOBBY_STATE.WON_LEVEL);
	if(everyoneFinished){
		sendSimpleMessageAll(SIMPLE_MESSAGE_TYPES.FINISH_END_COUNTDOWN);
		multiplayerState.endTime = 1;
	}
}

const handleReceiveChatMessage = ({peer, buffer}) => {
	const message = normalizeChatMessage(dataFromChatMessageBuffer(buffer).message, Settings);
	if (!message) return;

	const player = multiplayerState.players[peer];
	if(player && player.introduced){
		const name = player.playerState.name
		let type = CHAT_AUTHOR_TYPES.DEFAULT;
		if(player.vip) type = CHAT_AUTHOR_TYPES.VIP;

		processChatMessage(name, type, player.admin, message);

		player.chatBox?.setText?.(message);
	}
}

// DEBUG STUFF
const debugWindow = document.createElement('div');
debugWindow.classList.add('multiplayerDebug');
debugWindow.style = `
	position: absolute;
	z-index: 9999;
	top:0;
	left:0;
	background: white;
`;
debugWindow.innerHTML = `
	<div>Lobby:<span class="lobbyText"></span></div>
	<div>peersConnected:<span class="peersConnectedText"></span></div>
	<div>Misc data:<span class="miscDataText"></span></div>
	<div>Last Package Sent:<span class="lastPackageSent"></span></div>
	<ul class="playerList"></ul>
`


let lobbyText = null;
let peersConnectedText = null;
let miscText = null;
let lastSendID = null;
let playerList = null;
let playerElement = document.createElement('li');
playerElement.innerHTML = `
<ul>
 <li>Name:<span class="nameText"></span></li>
 <li>Connected:<span class="connectedText"></span></li>
 <li>PackageID:<span class="packageIDText"></span></li>
 <li>Info:<span class="infoText"></span></li>
 </ul>
`;


const updateDebugData = () =>{
	if(!lobbyText){
		lobbyText = debugWindow.querySelector('.lobbyText');
		peersConnectedText = debugWindow.querySelector('.peersConnectedText');
		miscText = debugWindow.querySelector('.miscDataText');
		lastSendID = debugWindow.querySelector('.lastPackageSent');
		playerList = debugWindow.querySelector('.playerList');
	}
	lobbyText.innerText = multiplayerState.lobby;
	peersConnectedText.innerText = multiplayerState.peersConnected;
	miscText.innerText = multiplayerState.misc;
	lastSendID.innerText = multiplayerState.sendPackageID;

	playerList.innerHTML = '';

	const playerIds = Object.keys(multiplayerState.players);
	playerIds.forEach(id => {
		const player = multiplayerState.players[id];
		const el = playerElement.cloneNode(true);
		playerList.appendChild(el);

		el.querySelector('.nameText').innerText = id;
		el.querySelector('.connectedText').innerText = player.connected.toString();
		el.querySelector('.packageIDText').innerText = player.lastPackageID.toString();
		el.querySelector('.infoText').innerHTML =
		`<ul>
			<li>X:${player.sprite.position.x}</li>
			<li>Y:${player.sprite.position.y}</li>
			<li>Ping:${player.ping}</li>
		</ul>`
	})
}
